import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { X, Library, Loader2 } from "lucide-react";

/**
 * Add a whole series at once.
 *
 * Pass either:
 *   - `seedKey` (e.g. "/works/OL82563W") to detect the series from one book, OR
 *   - `seriesName` + `author` to fetch by name (fallback).
 *
 * The modal calls /api/books/series/resolve to preview the candidate list,
 * lets the user pick which to add, and bulk-adds via /api/books/series.
 */
export function SeriesAddModal({ open, onClose, onAdded, seedKey, seriesName, author }) {
  const [resolving, setResolving] = useState(true);
  const [series, setSeries] = useState("");
  const [books, setBooks] = useState([]);
  const [message, setMessage] = useState("");
  const [picked, setPicked] = useState(() => new Set());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setResolving(true);
    setSeries(""); setBooks([]); setMessage(""); setPicked(new Set());
    const payload = seedKey
      ? { openlibrary_key: seedKey }
      : { series_name: seriesName || "", author: author || "" };
    api.post("/books/series/resolve", payload)
      .then(({ data }) => {
        setSeries(data.series_name || "");
        setBooks(data.books || []);
        setMessage(data.message || "");
        // Default-select all books that aren't already in the user's library.
        setPicked(new Set((data.books || []).filter((b) => !b.already_added).map((b) => b.openlibrary_key)));
      })
      .catch((e) => { setMessage(formatApiError(e)); })
      .finally(() => setResolving(false));
  }, [open, seedKey, seriesName, author]);

  if (!open) return null;
  const togglePick = (key) => setPicked((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const submit = async () => {
    if (picked.size === 0) return;
    setBusy(true);
    try {
      const chosen = books.filter((b) => picked.has(b.openlibrary_key));
      const { data } = await api.post("/books/series", {
        series_name: series,
        books: chosen,
      });
      const msg = `Added ${data.created} ${data.created === 1 ? "book" : "books"} from "${series}"` +
                  (data.skipped ? `. ${data.skipped} skipped (already in catalogue).` : ".");
      toast.success(msg);
      if (data.hit_limit) {
        toast.message("You've hit the free-tier 10-book cap. Upgrade to Reads+ to keep adding.");
      }
      onAdded?.(data);
      onClose?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 backdrop-blur-sm" onClick={onClose} data-testid="series-modal">
      <div className="bg-[#F2EAD3] max-w-2xl w-full max-h-[80vh] flex flex-col border border-[rgba(10,10,10,0.10)] shadow-[0_20px_60px_rgba(10,10,10,0.20)]" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between p-6 border-b border-[rgba(10,10,10,0.08)] bg-[#F2EAD3] flex-shrink-0">
          <div>
            <div className="font-mono uppercase tracking-[0.25em] text-[10px] text-[#525252] mb-2 inline-flex items-center gap-2"><Library className="w-3 h-3" strokeWidth={1.5} /> Add a whole series</div>
            <h3 className="font-heading text-2xl text-[#0A0A0A] tracking-tight">{series || "Detecting series…"}</h3>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-black/5 rounded-sm" aria-label="Close" data-testid="series-close">
            <X className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>

        <div className={`p-6 overflow-y-auto flex-1 ${resolving || !series || books.length === 0 ? "flex items-center justify-center min-h-[280px]" : ""}`}>
          {resolving ? (
            <div className="text-center font-mono uppercase tracking-widest text-xs text-[#525252]">
              <Loader2 className="w-6 h-6 animate-spin mx-auto mb-3 text-[#0A0A0A]" strokeWidth={1.5} />
              <div>Looking up books in this series…</div>
              <div className="text-[10px] text-[#8C7F65] mt-2 normal-case tracking-normal font-sans">Reading the Open Library catalogue</div>
            </div>
          ) : !series || books.length === 0 ? (
            <div className="text-center text-sm text-[#525252] max-w-sm" data-testid="series-no-match">
              {message || "Couldn't detect a series for this book. Open Library doesn't list it as part of a known series."}
            </div>
          ) : (
            <>
              <p className="text-sm text-[#0A0A0A] mb-4 leading-relaxed">
                Found <strong>{books.length}</strong> {books.length === 1 ? "book" : "books"} in <em>"{series}"</em>. Uncheck any you don't want — selected books are added unread so you can rate them as you finish.
              </p>
              <ul className="space-y-2 mb-6" data-testid="series-book-list">
                {books.map((b) => {
                  const checked = picked.has(b.openlibrary_key);
                  return (
                    <li
                      key={b.openlibrary_key}
                      className={`flex items-center gap-3 p-3 border rounded-sm transition-colors ${
                        b.already_added
                          ? "bg-[#E8DFC1] border-transparent opacity-60"
                          : checked
                            ? "bg-[#FAF5E6] border-[#0A0A0A]"
                            : "bg-[#FAF5E6] border-[rgba(10,10,10,0.10)]"
                      }`}
                      data-testid={`series-row-${b.openlibrary_key.replace(/\W/g, '_')}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={b.already_added}
                        onChange={() => togglePick(b.openlibrary_key)}
                        className="w-4 h-4 accent-[#0A0A0A] flex-shrink-0"
                        data-testid={`series-check-${b.openlibrary_key.replace(/\W/g, '_')}`}
                      />
                      <div className="w-10 h-14 flex-shrink-0 bg-[#E8DFC1] border border-[rgba(10,10,10,0.10)] overflow-hidden">
                        {b.cover_url && <img src={b.cover_url} alt="" loading="lazy" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-heading text-base text-[#0A0A0A] line-clamp-1">{b.title}</div>
                        <div className="font-mono uppercase tracking-widest text-[10px] text-[#525252] mt-0.5">
                          {b.author}{b.year ? ` · ${b.year}` : ""}{b.already_added ? " · Already in library" : ""}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
              <div className="flex items-center justify-between gap-3 pt-4 border-t border-[rgba(10,10,10,0.08)]">
                <span className="font-mono uppercase tracking-widest text-[10px] text-[#525252]" data-testid="series-pick-count">
                  {picked.size} selected
                </span>
                <button
                  onClick={submit}
                  disabled={busy || picked.size === 0}
                  className="inline-flex items-center gap-2 bg-[#0A0A0A] hover:bg-[#1F1F1F] text-[#F2EAD3] font-mono uppercase tracking-widest text-xs px-5 py-3 rounded-sm transition-colors disabled:opacity-50"
                  data-testid="series-add-submit"
                >
                  {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={1.5} /> : <Library className="w-3.5 h-3.5" strokeWidth={1.5} />}
                  Add {picked.size} {picked.size === 1 ? "book" : "books"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
